import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, ClipboardList, ArrowRight, ChevronDown, MapPin, Building2,
  Monitor, CreditCard, Package, Cable, Paperclip, Megaphone, Wrench, RotateCcw,
} from 'lucide-react';
import { displayOrderNumber } from '@platform/shared-types';
import { Skeleton, SkeletonRows } from '../../components/Skeleton';
import ShimmerImage from '../../components/ShimmerImage';
import { api } from '../../services/apiService';
import {
  hardwareApi, orderBillingLabel, shortDate, CATEGORY_LABEL,
  UNIT_STATUS_LABEL, UNIT_STATUS_CHIP, ORDER_STATUS_LABEL, ORDER_STATUS_CHIP,
  type DeviceOrderRow, type DeviceUnitRow, type DeviceModelRow,
} from '../../services/hardwareApi';
// The OS chip and the checklist are the SAME components the order screen's
// POS lane draws — see UnitProvisioning.tsx. This list and that lane are two
// views of one piece of work, and a step ticked here has to read as ticked
// there.
import { OsStatusChip, UnitChecklist } from '../inventory/UnitProvisioning';
import { CreateRegisterPanel } from '../inventory/DeviceSetupPanels';
// The same quick view the set-up lanes at the bottom of the card open, so a
// device cannot be described one way up here and another way down there.
import UnitQuickView from '../inventory/UnitQuickView';
// The order screen's own step rail and problem menu, brought to the bench —
// a problem is learned while holding the device, not while reading an order.
import DeviceStatusPanel from '../inventory/DeviceStatusPanel';

/**
 * The stages this bench works: configuring the device, then coupling it to its
 * reader. All three configure stages are named so the filter holds if a
 * non-POS device ever reaches this queue — a device only ever has one of them.
 */
const PENDING_STAGES = ['provisioned', 'provision_payment', 'provision_accessories', 'device_couple'] as const;

/**
 * PENDING CONFIGURATION — takes the old pos-config "Catalog" tab slot.
 *
 * Catalog/bundle EDITING moved to /inventory/catalog (Stock tab) + /inventory/bundles-editor
 * (that's inventory business, not a device config); what actually belongs on
 * a "Device Configurations" page is this: which orders have physical units
 * sitting there waiting to be configured. Derived entirely client-side from
 * the existing `listOrders` + `listUnits` (paginated, one call per waiting
 * state) — no new backend endpoint.
 *
 * "Waiting for configuration" = a unit allocated to an order (`allocated`)
 * or actively mid-checklist (`provisioning`) — see b1-lifecycle-report.md's
 * state machine. A unit that hasn't reached `allocated` yet isn't on an
 * order; a unit past `configured` is already done.
 *
 * EVERY IDENTIFIER ON THIS SCREEN READS AS A NAME, not as a key: the order by
 * its `orderNo`, the tenant by its business name, the store by its own name,
 * the device by its photo and model. The raw ids stay — an operator on the
 * phone to a merchant still needs to read one out — but they sit on the muted
 * meta line rather than being the only thing the card says.
 */

type WaitingState = 'allocated' | 'provisioning';
const WAITING_STATES: WaitingState[] = ['allocated', 'provisioning'];
const PAGE_SIZE = 50;

/** How many device rows an order shows before the rest collapse behind one button. */
const VISIBLE_UNITS = 5;

/** ONE grid for the column labels and every device row, so the two cannot drift apart. */
const UNIT_GRID =
  'grid items-center gap-3 grid-cols-[minmax(0,1fr)_104px_122px] md:grid-cols-[minmax(0,1fr)_112px_100px_116px_120px_118px]';

/** The one category this tab configures — `CATEGORY_LABEL` calls it "POS device". */
const POS_CATEGORY = 'pos_terminal';

/**
 * IS THIS UNIT HERE BECAUSE SOMEBODY SENT IT BACK?
 *
 * The stamp is never cleared, so presence alone says nothing — a request older
 * than the install was already carried out. Same comparison the service makes
 * in `osInstallGate`, so the queue and the gate agree about which units are
 * waiting to be built again.
 */
const outstandingReinstall = (u: DeviceUnitRow): boolean =>
  !!u.reinstall && u.reinstall.requestedAt > (u.osInstall?.installedAt ?? 0);

const CATEGORY_ICON: Record<string, React.FC<any>> = {
  pos_terminal: Monitor,
  payment_device: CreditCard,
  peripheral: Package,
  accessory: Cable,
  office_supply: Paperclip,
  marketing_material: Megaphone,
  tools: Wrench,
};

/** The fallback label for an order placed before `orderNo` existed. */
const shortRef = (orderId: string): string =>
  `#${String(orderId).replace(/^ord_/, '').slice(0, 8).toUpperCase()}`;

/** `ord_ee3108cd76a6921f7ed65484` → `ord_ee3108cd…65484`, still copyable by eye. */
const elideId = (orderId: string): string =>
  orderId.length > 22 ? `${orderId.slice(0, 12)}…${orderId.slice(-5)}` : orderId;

interface StatePage {
  units: DeviceUnitRow[];
  nextToken?: string;
  loading: boolean;
  error: string | null;
}

const emptyPage = (): StatePage => ({ units: [], loading: false, error: null });

const provisioningProgress = (u: DeviceUnitRow): { done: number; total: number } | null => {
  const steps = u.provisioning?.steps;
  if (!steps || steps.length === 0) return null;
  const required = steps.filter((s) => s.required);
  if (required.length > 0) return { done: required.filter((s) => s.done).length, total: required.length };
  return { done: steps.filter((s) => s.done).length, total: steps.length };
};

/**
 * Past this many steps one pip per step no longer fits the Provisioning column.
 * A pip is 8px wide on a 2px gap, so 20 steps — what a real unit carries — want
 * 200px in a 132px column and used to spill over the action button.
 */
const MAX_PIPS = 10;

/** Provisioning as a shape, not a fraction — how far along is legible without reading. */
const ProgressPips: React.FC<{ done: number; total: number }> = ({ done, total }) => (
  <div className="flex min-w-0 items-center gap-2">
    {total <= MAX_PIPS ? (
      <span className="flex gap-0.5" aria-hidden="true">
        {Array.from({ length: total }, (_, i) => (
          <i key={i} className={`block h-1 w-2 rounded-sm ${i < done ? 'bg-rose-600' : 'bg-gray-200'}`} />
        ))}
      </span>
    ) : (
      // Too many steps to draw one each: same shape, one proportional track.
      <span className="h-1 min-w-0 flex-1 overflow-hidden rounded-sm bg-gray-200" aria-hidden="true">
        <i
          className="block h-1 rounded-sm bg-rose-600"
          style={{ width: `${total > 0 ? Math.round((done / total) * 100) : 0}%` }}
        />
      </span>
    )}
    <span className="shrink-0 text-[11.5px] tabular-nums text-gray-600">{done}/{total}</span>
  </div>
);

const PendingConfigurationTab: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<DeviceOrderRow[]>([]);
  const [models, setModels] = useState<DeviceModelRow[]>([]);
  const [tenantNames, setTenantNames] = useState<Record<string, string>>({});
  const [locationNames, setLocationNames] = useState<Record<string, string>>({});
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [pages, setPages] = useState<Record<WaitingState, StatePage>>({
    allocated: emptyPage(),
    provisioning: emptyPage(),
  });
  const [initialLoading, setInitialLoading] = useState(true);
  /** Which orders the operator has opened past the first `VISIBLE_UNITS` rows. */
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  /**
   * Which devices have their checklist open.
   *
   * A SET, not one-at-a-time: a bench with three tills on the same order is
   * worked side by side, and closing the one you just ticked to look at the
   * next is the interaction nobody wants.
   */
  const [openUnits, setOpenUnits] = useState<Set<string>>(new Set());
  /**
   * Which device's quick view is open.
   *
   * SEPARATE from `openUnits` above, and deliberately: expanding a row is
   * "I am going to work on this", the popup is "what IS this" — two different
   * questions, and making the chevron do both would mean every glance costs a
   * checklist fetch.
   */
  const [peek, setPeek] = useState<string | null>(null);
  const toggleUnit = useCallback((unitId: string) => {
    setOpenUnits((prev) => {
      const next = new Set(prev);
      if (next.has(unitId)) next.delete(unitId); else next.add(unitId);
      return next;
    });
  }, []);

  /**
   * ONE UNIT, AS THE SERVICE JUST RETURNED IT.
   *
   * Replaces the row in place rather than refetching both state pages: a
   * reload would remount every checklist on the screen and throw away the
   * reader's place on each tick.
   *
   * A unit that ticks its way to 'configured' has left the waiting states this
   * page lists, and it is deliberately NOT dropped from the list here — it
   * stays, showing Configured, so the last tick reads as completion rather
   * than as the row vanishing mid-interaction. The next load drops it.
   */
  const applyUnit = useCallback((unit: DeviceUnitRow) => {
    setPages((prev) => {
      const next = { ...prev };
      for (const state of WAITING_STATES) {
        const page = next[state];
        if (!page.units.some((u) => u.unitId === unit.unitId)) continue;
        next[state] = { ...page, units: page.units.map((u) => (u.unitId === unit.unitId ? unit : u)) };
      }
      return next;
    });
  }, []);

  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    setOrdersError(null);
    try {
      const [o, m] = await Promise.all([
        hardwareApi.listOrders(),
        hardwareApi.listModels().catch(() => ({ items: [] as DeviceModelRow[] })),
      ]);
      setOrders(o.items || []);
      setModels(m.items || []);
    } catch (e: any) {
      setOrdersError(e?.message || 'Failed to load orders');
    } finally {
      setOrdersLoading(false);
    }
    // Tenant names come from a different service — best-effort, an order still
    // reads correctly by raw tenantId if this is unavailable.
    try {
      const tenants = await api.tenants.getAll();
      const names: Record<string, string> = {};
      for (const t of tenants || []) names[t.id] = t.name || t.businessName || t.id;
      setTenantNames(names);
    } catch { /* fall back to raw tenantId */ }
  }, []);

  const loadState = useCallback(async (state: WaitingState, nextToken?: string) => {
    setPages((prev) => ({ ...prev, [state]: { ...prev[state], loading: true, error: nextToken ? prev[state].error : null } }));
    try {
      const res = await hardwareApi.listUnits({ state, limit: PAGE_SIZE, nextToken });
      setPages((prev) => ({
        ...prev,
        [state]: {
          units: nextToken ? [...prev[state].units, ...(res.items || [])] : (res.items || []),
          nextToken: res.nextToken,
          loading: false,
          error: null,
        },
      }));
    } catch (e: any) {
      setPages((prev) => ({ ...prev, [state]: { ...prev[state], loading: false, error: e?.message || `Failed to load ${state} units` } }));
    }
  }, []);

  useEffect(() => {
    // Orders and units both feed the same table (a unit's card needs its
    // order's tenant/items) — wait for both before the first paint so rows
    // never flash "unknown tenant" while the orders call is still in flight.
    Promise.all([loadOrders(), ...WAITING_STATES.map((s) => loadState(s))]).finally(() => setInitialLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const modelOf = useCallback(
    (modelId?: string) => (modelId ? models.find((m) => m.modelId === modelId) : undefined),
    [models],
  );

  const waitingUnits = useMemo(
    () => [...pages.allocated.units, ...pages.provisioning.units],
    [pages],
  );

  const ordersById = useMemo(() => new Map(orders.map((o) => [o.orderId, o])), [orders]);

  /**
   * This tab is POS devices ONLY. A payment pin pad, a cable or a marketing
   * insert on the same order is configured elsewhere and must never show here.
   *
   * A unit whose model has NOT loaded is KEPT: `listModels` is allowed to fail
   * (it is caught to an empty list), and hiding every row on that failure would
   * read as "nothing waiting" rather than "the catalog is unavailable".
   */
  const isPosUnit = useCallback(
    (u: DeviceUnitRow) => {
      const model = modelOf(u.modelId);
      return !model || model.category === POS_CATEGORY;
    },
    [modelOf],
  );

  const rows = useMemo(() => {
    const byOrder = new Map<string, DeviceUnitRow[]>();
    for (const u of waitingUnits) {
      if (!u.orderId) continue; // allocated/provisioning always carries an orderId in practice, but never drop silently on the assumption
      if (!isPosUnit(u)) continue;
      if (!byOrder.has(u.orderId)) byOrder.set(u.orderId, []);
      byOrder.get(u.orderId)!.push(u);
    }
    return Array.from(byOrder.entries())
      .map(([orderId, units]) => ({ orderId, order: ordersById.get(orderId), units }))
      .sort((a, b) => (b.order?.createdAt || 0) - (a.order?.createdAt || 0));
  }, [waitingUnits, ordersById, isPosUnit]);

  /**
   * STORE NAMES.
   *
   * An order carries a `locationId` and nothing legible, and there is no
   * cross-tenant location list — the name lives on the location's onboarding
   * request, listed per tenant. One call per DISTINCT tenant on screen (a
   * handful in practice), each fetched once, and a failure is silent: the id
   * renders instead, which is what the card showed before.
   *
   * This effect MUST NOT cancel its fetch on cleanup. `tenantsFetched` is a
   * ref, so it survives both StrictMode's double-invoke and the several
   * identity changes of `rows` during load (the two `loadState` calls and
   * `loadOrders` each set state independently) — a cancelled call is therefore
   * never retried and the name is lost for good, which is exactly why every
   * card used to render a raw `loc_…` id. React 18 does not warn on a setState
   * after unmount, so the late write is harmless. A tenant whose call FAILED is
   * un-marked so a later render can try it again.
   */
  const tenantsFetched = useRef<Set<string>>(new Set());

  useEffect(() => {
    const pending = Array.from(
      new Set(rows.map((r) => r.order?.tenantId).filter((t): t is string => !!t)),
    ).filter((t) => !tenantsFetched.current.has(t));
    if (pending.length === 0) return;
    pending.forEach((t) => tenantsFetched.current.add(t));

    (async () => {
      const found: Record<string, string> = {};
      await Promise.all(pending.map(async (tenantId) => {
        try {
          const data: any = await api.locationBilling.listTenantRequests(tenantId);
          const list: any[] = Array.isArray(data) ? data : data?.items || data?.requests || [];
          for (const r of list) {
            if (r?.locationId) found[r.locationId] = r.name || r.legalEntityName || r.locationId;
          }
        } catch {
          // the locationId renders instead — let a later render retry this tenant
          tenantsFetched.current.delete(tenantId);
        }
      }));
      if (Object.keys(found).length > 0) {
        setLocationNames((prev) => ({ ...prev, ...found }));
      }
    })();
  }, [rows]);

  const configure = (unitId: string) => navigate(`/pos-config?tab=install&unitId=${encodeURIComponent(unitId)}`);
  const openOrder = (orderId: string) => navigate(`/inventory/orders?open=${encodeURIComponent(orderId)}`);

  if (initialLoading) {
    return (
      <div>
        <Skeleton variant="light" className="h-4 w-80 max-w-full mb-4" />
        <div className="space-y-3">
          {[0, 1].map((i) => (
            <div key={i} className="rounded-xl border border-gray-200 bg-white p-4">
              <SkeletonRows variant="light" count={2} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  /* Looked up across BOTH waiting pages: the popup is opened from any row on
     the screen, and a device is only ever in one of them. */
  const peeked = peek ? waitingUnits.find((u) => u.unitId === peek) : undefined;

  return (
    <div>
      {peeked && (
        <UnitQuickView
          unit={peeked}
          model={modelOf(peeked.modelId)}
          siblings={waitingUnits}
          onClose={() => setPeek(null)}
          onOpenUnit={(id) => setPeek(id)}
        />
      )}
      {(ordersError || pages.allocated.error || pages.provisioning.error) && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-600" />
          <div className="text-sm text-red-800">
            {[ordersError, pages.allocated.error, pages.provisioning.error].filter(Boolean).join(' · ')}
          </div>
        </div>
      )}

      <p className="mb-5 max-w-[68ch] text-sm text-gray-600">
        Orders with a <strong>POS device</strong> allocated but not yet set up — pin pads, accessories and
        supplies on the same order are configured elsewhere. Configuring a unit is done from the <strong>Install</strong> tab.
      </p>

      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white py-16 text-center">
          <ClipboardList size={28} className="mx-auto mb-3 text-gray-300" />
          <p className="text-sm font-medium text-gray-700">No orders waiting for configuration</p>
          <p className="mt-1 text-xs text-gray-500">
            An order shows up here once a POS device is allocated to it and hasn't been fully configured yet.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {rows.map(({ orderId, order, units }) => {
            const isOpen = !!expanded[orderId];
            const overflow = Math.max(0, units.length - VISIBLE_UNITS);
            const shown = isOpen ? units : units.slice(0, VISIBLE_UNITS);

            // What the order holds in total vs what is waiting here. The two
            // differ once a device has been configured AND whenever the order
            // carries anything that is not a POS device, so the remainder
            // cannot be called "waiting" — it is only ever "in this order".
            const totalUnits = order?.unitIds?.length || units.length;
            const posCount = `${units.length} POS device${units.length === 1 ? '' : 's'}`;
            const deviceCount = totalUnits > units.length
              ? `${posCount} of ${totalUnits} in this order`
              : posCount;

            const storeName = order?.locationId
              ? locationNames[order.locationId]
              : undefined;

            return (
              <section key={orderId} className="rounded-xl border border-gray-200 bg-white">
                <header className="p-4 pb-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          onClick={() => openOrder(orderId)}
                          className="font-mono text-[17px] font-bold tabular-nums tracking-tight text-emerald-700 hover:underline"
                        >
                          {order?.orderNo ? displayOrderNumber(order.orderNo) : shortRef(orderId)}
                        </button>
                        {order && (
                          <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${ORDER_STATUS_CHIP[order.status] || 'bg-gray-200 text-gray-700'}`}>
                            {ORDER_STATUS_LABEL[order.status] || order.status}
                          </span>
                        )}
                        {order?.partial && (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-amber-800">PARTIAL</span>
                        )}
                      </div>

                      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
                        <span className="inline-flex items-center gap-1.5 font-semibold text-gray-900">
                          <Building2 size={13} className="shrink-0 text-gray-500" />
                          {order ? (tenantNames[order.tenantId] || order.tenantId) : 'Unknown business'}
                        </span>
                        {order?.locationId && (
                          <>
                            <span className="text-gray-400">·</span>
                            <span
                              className={`inline-flex items-center gap-1.5 ${storeName ? 'text-gray-600' : 'font-mono text-[12px] text-gray-400'}`}
                              title={storeName ? order.locationId : 'Store name unavailable — showing the location id'}
                            >
                              <MapPin size={13} className="shrink-0" />
                              {storeName || order.locationId}
                            </span>
                          </>
                        )}
                      </div>

                      <p className="mt-1 text-[11.5px] tabular-nums text-gray-400">
                        {order ? `Placed ${shortDate(order.createdAt)} · ` : ''}
                        {deviceCount}
                        {order ? ` · ${orderBillingLabel(order.items)}` : ''}
                        <span className="font-mono"> · {elideId(orderId)}</span>
                      </p>
                    </div>

                    <button
                      onClick={() => openOrder(orderId)}
                      className="inline-flex min-h-[34px] items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-[11.5px] font-semibold text-gray-600 hover:bg-gray-50"
                    >
                      Open order <ArrowRight size={12} />
                    </button>
                  </div>
                </header>

                <div className={`${UNIT_GRID} px-4 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400`}>
                  <div>Device</div>
                  <div className="hidden md:block">Unit</div>
                  <div>State</div>
                  {/* The INSTALL and the CHECKLIST are two different questions and a till
                      can be stuck on either — "0/20" alone never said which. */}
                  <div className="hidden md:block">RubyTill</div>
                  <div className="hidden md:block">Provisioning</div>
                  <div className="pr-0.5 text-right">Action</div>
                </div>

                <div>
                  {shown.map((u) => {
                    const model = modelOf(u.modelId);
                    const Icon = CATEGORY_ICON[model?.category || ''] || Package;
                    const progress = provisioningProgress(u);
                    const mid = u.state === 'provisioning';

                    const isUnitOpen = openUnits.has(u.unitId);

                    return (
                      <div key={u.unitId} className="border-t border-gray-100">
                      <div className={`${UNIT_GRID} px-4 py-2.5 hover:bg-gray-50/60`}>
                        <div className="flex min-w-0 items-center gap-3">
                          <button
                            type="button"
                            onClick={() => setPeek(u.unitId)}
                            aria-label={`Quick info for ${u.trackingId || u.unitId}`}
                            className="shrink-0 rounded-[10px] hover:opacity-80"
                          >
                            <ShimmerImage
                              src={model?.media?.heroImageUrl}
                              alt={model?.modelName || u.modelId}
                              width={44}
                              height={44}
                              objectFit="contain"
                              className="rounded-[10px] border border-gray-200"
                              fallbackBgColor="bg-gray-100"
                              fallbackIcon={<Icon size={19} className="text-gray-400" />}
                            />
                          </button>
                          {/*
                            TWO CONTROLS, BECAUSE THERE ARE TWO QUESTIONS.

                            The NAME opens the quick view — "what is this
                            device" — and the CHEVRON expands the checklist —
                            "I am going to work on this". They used to be one
                            control, which meant every glance at a device cost
                            a checklist fetch and a remount of everything
                            inside it.
                          */}
                          <button
                            type="button"
                            onClick={() => setPeek(u.unitId)}
                            title={`Quick info — ${u.trackingId || u.serialNumber || u.unitId}`}
                            className="min-w-0 flex-1 text-left hover:opacity-80"
                          >
                            <span className="block truncate text-[13px] font-medium text-gray-900 underline-offset-2 hover:underline">
                              {model?.modelName || u.modelId}
                            </span>
                            <span className="block truncate text-[11px] text-gray-400">
                              {[model?.manufacturer, model?.category ? CATEGORY_LABEL[model.category] : null]
                                .filter(Boolean).join(' · ') || '—'}
                            </span>
                          </button>
                          {/*
                            A LABELLED BUTTON, NOT A CARET.

                            This was a 13px grey chevron floating between two
                            columns, which reads as decoration — the checklist
                            is the main thing you do on this screen and people
                            could not find the way into it. It is now the size
                            and weight of a control, says what it opens, and
                            turns emerald while open so an expanded row is
                            obvious from across the table.
                          */}
                          <button
                            type="button"
                            onClick={() => toggleUnit(u.unitId)}
                            aria-expanded={isUnitOpen}
                            className={`inline-flex min-h-[36px] shrink-0 items-center gap-1.5 rounded-lg border px-2.5 text-[11px] font-bold transition-colors ${
                              isUnitOpen
                                ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                                : 'border-gray-300 bg-white text-gray-600 hover:border-emerald-400 hover:text-emerald-700'
                            }`}
                          >
                            <ChevronDown
                              size={13}
                              className={`transition-transform ${isUnitOpen ? 'rotate-180' : ''}`}
                            />
                            {isUnitOpen ? 'Hide' : 'Checklist'}
                          </button>
                        </div>

                        <div className="hidden font-mono text-[11.5px] tabular-nums text-gray-600 md:block">
                          {u.trackingId || u.serialNumber || u.unitId}
                          {/* WHY it came back, beside the device it came back
                              on — so the next person does not reinstall the
                              same thing the same way. */}
                          {outstandingReinstall(u) && u.reinstall?.reason && (
                            <span className="mt-0.5 block font-sans text-[10.5px] leading-snug text-amber-700">
                              {u.reinstall.reason}
                            </span>
                          )}
                        </div>

                        <div>
                          {/*
                            A REINSTALL LOOKS EXACTLY LIKE A NEW MACHINE
                            otherwise. The state is "Allocated" either way, and
                            the difference decides whether somebody should be
                            surprised that a configured till is back in the
                            queue — so the queue says which it is.
                          */}
                          {outstandingReinstall(u) ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                              <RotateCcw size={9} /> Reinstall
                            </span>
                          ) : (
                            <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${UNIT_STATUS_CHIP[u.state]}`}>
                              {UNIT_STATUS_LABEL[u.state]}
                            </span>
                          )}
                        </div>

                        <div className="hidden min-w-0 md:block">
                          <OsStatusChip unit={u} model={model} />
                        </div>

                        <div className="hidden min-w-0 overflow-hidden md:block">
                          {progress
                            ? <ProgressPips done={progress.done} total={progress.total} />
                            : <span className="text-[11.5px] text-gray-400">Not started</span>}
                        </div>

                        <div>
                          <button
                            onClick={() => configure(u.unitId)}
                            className="inline-flex min-h-[34px] w-full items-center justify-center gap-1.5 rounded-lg bg-emerald-500 text-[11px] font-semibold text-white hover:bg-emerald-700"
                          >
                            {mid ? 'Continue' : outstandingReinstall(u) ? 'Reinstall' : 'Configure'} <ArrowRight size={11} />
                          </button>
                        </div>
                      </div>

                      {/*
                        THE CHECKLIST, WORKED FROM HERE.

                        The same component the order screen draws, so a step
                        ticked on either reads as ticked on both — and the OS
                        gate locks these rows in exactly the same way, because
                        it is the same gate.

                        Mounted only when opened: the plan is fetched per
                        model, and loading every one on page load would cost a
                        request per row for checklists nobody asked to see.
                      */}
                      {isUnitOpen && (
                        <div className="space-y-3 border-t border-gray-100 bg-gray-50/60 px-4 py-3">
                          {/*
                            WHERE IT IS, BEFORE WHAT IS LEFT TO DO.

                            A device with a problem is not a device whose
                            checklist anyone should be working, so the rail and
                            the problem menu come first and the checklist reads
                            as the next question rather than the only one.
                          */}
                          <DeviceStatusPanel
                            unit={u}
                            orderStatus={order?.status}
                            onUnitChanged={applyUnit}
                          />

                          {/*
                            THE BENCH'S OWN TWO STAGES, not the whole plan.

                            This queue is "what is on the bench today": the
                            device is configured and then coupled to its
                            reader, and that is the entire job at this station.
                            Allocation happens in the warehouse, and the
                            testing, packing and pre-ship checks are worked on
                            the ORDER, where the whole kit is together — so
                            drawing them here would be asking one person to
                            tick work they are not in a position to do.
                          */}
                          <UnitChecklist
                            unit={u}
                            model={model}
                            stageIds={PENDING_STAGES}
                            emptyNote="No configuring or coupling steps on this device’s checklist. The rest of its plan is worked on the order."
                            onUnitChanged={applyUnit}
                          />

                          {/*
                            THE STEP AFTER THE CHECKLIST, in the place the
                            checklist is worked.

                            Install RubyTill, work the list, create the register
                            — that is one sitting at one bench, and sending
                            somebody to the order screen for the last of the
                            three was the reason it got done out of order. The
                            panel gates itself (`registerBlockReason`), so this
                            is not a second opinion about when it is allowed.

                            Scoped to THIS unit: the panel takes a list, and
                            handing it the whole order here would draw every
                            device again inside one device's row.
                          */}
                          {order?.tenantId && (
                            <CreateRegisterPanel
                              /* The SAME stages the checklist above draws, so
                                 the gate counts the work this bench can see
                                 and not the packing and pre-ship steps worked
                                 on the order screen. */
                              stageIds={PENDING_STAGES}
                              tenantId={order.tenantId}
                              tenantName={tenantNames[order.tenantId]}
                              locationId={order.locationId}
                              locationName={storeName}
                              units={[u]}
                              models={models}
                              onChanged={() => loadState(u.state as WaitingState)}
                            />
                          )}
                        </div>
                      )}
                      </div>
                    );
                  })}
                </div>

                {overflow > 0 && (
                  <button
                    onClick={() => setExpanded((prev) => ({ ...prev, [orderId]: !prev[orderId] }))}
                    aria-expanded={isOpen}
                    className="flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-b-xl border-t border-gray-100 bg-gray-50/70 text-[11.5px] font-semibold text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                  >
                    {isOpen ? 'Show fewer' : `Show ${overflow} more device${overflow === 1 ? '' : 's'}`}
                    <ChevronDown size={13} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                )}
              </section>
            );
          })}
        </div>
      )}

      {(pages.allocated.nextToken || pages.provisioning.nextToken) && (
        <div className="mt-4 flex flex-wrap gap-2">
          {pages.allocated.nextToken && (
            <button
              onClick={() => loadState('allocated', pages.allocated.nextToken)}
              disabled={pages.allocated.loading}
              className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {pages.allocated.loading ? <RubyGemMark size={12} drawing color="currentColor" /> : null} Load more allocated units
            </button>
          )}
          {pages.provisioning.nextToken && (
            <button
              onClick={() => loadState('provisioning', pages.provisioning.nextToken)}
              disabled={pages.provisioning.loading}
              className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {pages.provisioning.loading ? <RubyGemMark size={12} drawing color="currentColor" /> : null} Load more provisioning units
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default PendingConfigurationTab;
